import {
  DocumentOrError,
  TaxDocument,
  TaxForm,
} from "../../../model/tax/tax_document";
import DocumentIcon from "../../../assets/document.svg";
import ChevronRight from "../../../assets/chevron_right.svg";
import FileUploadIcon from "../../../assets/file_upload_icon.svg";
import { RootState } from "../../../store";
import { toDollar } from "../../../utils/dataConverter";
import { connect, useDispatch } from "react-redux";
import { taxSlice } from "../../../store/reducer/tax";
import { ErrorAlert } from "../../../components/Alerts";

interface DocumentListProps<T extends TaxForm> {
  taxState: RootState["tax"];
  formName: string;
  documents: DocumentOrError<T>[];
  title:
  | string
  | ((document: TaxDocument<T>) => string | null | undefined)
  | null
  | undefined;
  properties: { name: string; prop: string | ((form: T) => number) }[];
  onDropAccepted: (files: FileList) => void;
  showUpload?: boolean;
}

const DocumentList = <T extends TaxForm>({
  taxState,
  documents,
  formName,
  title,
  onDropAccepted,
  properties,
  showUpload = true,
}: DocumentListProps<T>) => {
  const selectUpload = () => {
    dispatch(taxSlice.actions.selectForm(-1));
    if (taxState.selectedFormIndex === -1) {
      const input = document.createElement("input");
      input.type = "file";
      input.onchange = (e) => {
        const files = (e.target as HTMLInputElement).files;
        if (files) onDropAccepted(files);
      };
      input.click();
    }
  };
  const dispatch = useDispatch();
  return (
    <div className="document-list">
      {documents.map((document, index) =>
        document.document_name === "error" ? (
          <ErrorAlert key={index}>{document.message}</ErrorAlert>
        ) : (
          <div
            key={index}
            tabIndex={0}
            className={
              "document-list-item" +
              (taxState.selectedFormIndex === index ? " active" : "")
            }
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                dispatch(taxSlice.actions.selectForm(index));
              }
            }}
            onClick={() => dispatch(taxSlice.actions.selectForm(index))}
          >
            <div className="content">
              <div className="title-row">
                <img src={DocumentIcon} alt="Document" />
                <span>
                  {title &&
                    (typeof title === "string" ? title : title(document))}
                </span>
              </div>
              <div className="data-row">
                {properties.map(({ name, prop }) => (
                  <div className="data-row-item">
                    <span className="item-title">{name}</span>
                    <br />
                    <span>
                      {toDollar(
                        typeof prop === "string"
                          ? Number(document.form_data[prop as keyof T])
                          : prop(document.form_data)
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <img src={ChevronRight} aria-hidden={true} alt="Right arrow" />
          </div>
        )
      )}

      {showUpload && (
        <div
          key={'upload'}
          tabIndex={0}
          className={
            "document-list-item" +
            (taxState.selectedFormIndex === -1 ? " active" : "")
          }
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              selectUpload();
            }
          }}
          onClick={selectUpload}
        >
          <div className="content">
            {!documents.length && (
              <div className="title-row">
                <img src={FileUploadIcon} aria-hidden={true} alt="Document" />
                Upload document
              </div>
            )}
            <div>
              {documents.length
                ? `Click here to upload next ${formName}`
                : `Click here to upload a ${formName} form.`}
            </div>
          </div>
          <img src={ChevronRight} alt="Right arrow" aria-hidden={true} />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  taxState: state.tax,
});

export default connect(mapStateToProps)(DocumentList);
